'use client';

import { useSuspenseQuery } from '@apollo/client/react';
import { FC, PropsWithChildren, useCallback, useMemo } from 'react';

import { GET_ME_IMAGE_GENERATIONS } from '@/.graphql';
import { SerializedFile, serializeFiles } from '@/src/utils/serializeFile';

import { createCustomContext } from './context';

interface IImageGenerationProps {
  imagesPublished: SerializedFile[];
  loadMoreImagesPublished: () => void;
  hasMoreImagesPublished: boolean;
  isLoadingImagesPublished: boolean;
  getImageById: (id: string) => SerializedFile | undefined;
}

const [ImageGenerationPublishedContext, useImageGenerationPublishedContext] =
  createCustomContext<IImageGenerationProps>({
    name: 'ImageGenerationPublishedContext',
    strict: false,
  });

export { useImageGenerationPublishedContext };

export const ImageGenerationPublishedProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    data: imageData,
    fetchMore: fetchMoreImages,
    networkStatus: imageNetworkStatus,
  } = useSuspenseQuery(GET_ME_IMAGE_GENERATIONS, {
    variables: {
      cursor: null,
      isPublished: true,
    },
  });

  const $data = useMemo(() => {
    const data = imageData?.getMeImageGenerations?.data || [];

    return data.map((imageGeneration) => {
      const images = imageGeneration.images || [];

      return {
        ...imageGeneration,
        images: images.filter((img) =>
          imageGeneration.publishedFileIds?.some((id) => img.includes(id)),
        ),
      };
    });
  }, [imageData]);

  // Мемоизируем сериализованные данные
  const imagesPublished = useMemo(() => serializeFiles($data), [$data]);

  // Получаем курсор
  const imageNextCursor = imageData?.getMeImageGenerations?.nextCursor ?? null;
  const hasMoreImagesPublished = !!imageNextCursor;

  // Статус загрузки для fetchMore
  const isLoadingImagesPublished = imageNetworkStatus === 3; // NetworkStatus.fetchMore

  const loadMoreImagesPublished = useCallback(() => {
    if (!hasMoreImagesPublished || isLoadingImagesPublished) {
      return;
    }

    return fetchMoreImages({
      variables: {
        cursor: imageNextCursor,
        isPublished: true,
      },
    });
  }, [
    hasMoreImagesPublished,
    isLoadingImagesPublished,
    imageNextCursor,
    fetchMoreImages,
  ]);

  // Utility functions
  const getImageById = useCallback(
    (id: string) =>
      imagesPublished.find(
        (img) => 'imageGenerationId' in img && img.imageGenerationId === id,
      ),
    [imagesPublished],
  );

  const contextValue = useMemo(
    () => ({
      imagesPublished,
      loadMoreImagesPublished,
      hasMoreImagesPublished,
      isLoadingImagesPublished,
      getImageById,
    }),
    [
      imagesPublished,
      loadMoreImagesPublished,
      hasMoreImagesPublished,
      isLoadingImagesPublished,
      getImageById,
    ],
  );

  return (
    <ImageGenerationPublishedContext value={contextValue}>
      {children}
    </ImageGenerationPublishedContext>
  );
};
